import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  CustomerRanking,
  REPORT_MAX_RANGE_DAYS,
  REPORT_PRESETS,
  ReportArea,
  ReportPreset,
  type CustomersReportQuery,
} from '@optiflow/types';
import { reportAreaVisible } from '@optiflow/shared';
import { Alert } from '@/components/ui/Alert';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { apiErrorCode } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { useCustomersReportQuery } from '../api/customersOptical.api';
import { CustomersContent } from '../components/CustomersContent';
import { ReportFilterBar, type ReportFilters } from '../components/ReportFilterBar';
import { ReportMetaLine } from '../components/ReportMetaLine';
import { ReportTabs } from '../components/ReportTabs';

const RANKINGS = Object.values(CustomerRanking);

/** The filters this page keeps in the URL, so a reading can be shared or reloaded unchanged. */
function readFilters(params: URLSearchParams) {
  const preset = params.get('preset');
  const ranking = params.get('ranking');
  return {
    period: {
      preset: (REPORT_PRESETS.includes(preset as ReportPreset)
        ? preset
        : ReportPreset.THIS_MONTH) as ReportPreset,
      from: params.get('from') ?? '',
      to: params.get('to') ?? '',
      branchId: params.get('branch') ?? '',
      compare: 'none' as const,
    } satisfies ReportFilters,
    ranking: (RANKINGS.includes(ranking as CustomerRanking)
      ? ranking
      : CustomerRanking.NET_SALES) as CustomerRanking,
  };
}

/**
 * Reports › Customers.
 *
 * Registrations need customers.view; spending and balances each need more, and a section missing
 * here is one this account may not see. Balances are as of now, so the filter bar offers no
 * comparison. The branch filter narrows registrations by registering branch and everything else by
 * the order's branch — the page says so rather than blur the two.
 */
export function CustomersReportPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const { period } = filters;
  const grants = useAuthStore((state) => state.user?.permissions);
  const allowed = reportAreaVisible(grants, ReportArea.CUSTOMERS);
  const customReady =
    period.preset !== ReportPreset.CUSTOM || (period.from !== '' && period.to !== '');

  const query: CustomersReportQuery = {
    preset: period.preset,
    ...(period.preset === ReportPreset.CUSTOM ? { from: period.from, to: period.to } : {}),
    branchId: period.branchId || undefined,
    ranking: filters.ranking === CustomerRanking.NET_SALES ? undefined : filters.ranking,
  };
  const report = useCustomersReportQuery(query, { enabled: customReady && allowed });

  const write = (next: Partial<ReturnType<typeof readFilters>>) => {
    const merged = { ...filters, ...next, period: { ...period, ...(next.period ?? {}) } };
    const search = new URLSearchParams();
    search.set('preset', merged.period.preset);
    if (merged.period.preset === ReportPreset.CUSTOM) {
      if (merged.period.from) search.set('from', merged.period.from);
      if (merged.period.to) search.set('to', merged.period.to);
    }
    if (merged.period.branchId) search.set('branch', merged.period.branchId);
    if (merged.ranking !== CustomerRanking.NET_SALES) search.set('ranking', merged.ranking);
    setParams(search, { replace: true });
  };

  const errorText = () => {
    const code = apiErrorCode(report.error);
    if (code === 'VALIDATION_FAILED') {
      return t('reports.invalidPeriod', { days: REPORT_MAX_RANGE_DAYS });
    }
    if (code === 'FORBIDDEN') return t('reports.customers.noAccess');
    return t('reports.loadError');
  };

  return (
    <div className="space-y-5">
      <PageHeader title={t('reports.customers.title')} subtitle={t('reports.customers.subtitle')} />
      <ReportTabs />
      {allowed ? null : <Alert tone="info">{t('reports.customers.noAccess')}</Alert>}
      {allowed ? (
        <>
          <ReportFilterBar
            value={period}
            onChange={(next) => write({ period: { ...next, compare: 'none' } })}
            hideCompare
          />
          {report.data?.sales ? (
            <Card className="p-4">
              <label
                htmlFor="customers-ranking"
                className="text-muted mb-1 block text-xs font-medium"
              >
                {t('reports.customers.ranking.label')}
              </label>
              <select
                id="customers-ranking"
                className="border-line bg-surface text-ink w-full max-w-xs rounded-lg border px-3 py-2 text-sm"
                value={filters.ranking}
                onChange={(event) => write({ ranking: event.target.value as CustomerRanking })}
              >
                {RANKINGS.map((ranking) => (
                  <option key={ranking} value={ranking}>
                    {t(`reports.customers.ranking.options.${ranking}`)}
                  </option>
                ))}
              </select>
            </Card>
          ) : null}
        </>
      ) : null}
      {report.data ? <ReportMetaLine meta={report.data.meta} /> : null}
      {period.branchId && allowed ? (
        <p className="text-muted text-xs">{t('reports.customers.branchFilterNote')}</p>
      ) : null}
      {!customReady && allowed ? (
        <Alert tone="info">{t('reports.filters.chooseDates')}</Alert>
      ) : null}
      {report.isError ? <Alert tone="error">{errorText()}</Alert> : null}
      {report.isPending && customReady && allowed ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-hidden>
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="bg-surface border-line h-28 animate-pulse rounded-xl border"
            />
          ))}
        </div>
      ) : null}
      {report.data ? <CustomersContent report={report.data} query={query} /> : null}
    </div>
  );
}
